import type { RangeHealthVm } from '../../api/types';

/**
 * F2 — provisioning stage inference for the ~10-minute deploy wait.
 *
 * The backend doesn't (yet) expose live Step Functions stage data, so we infer
 * a coarse "Network → Instances → SSM online → Detection → Ready" progression
 * from the per-VM health poll (how many launched, how many have their SSM agent
 * online) plus the range status. Heuristic by design — the ETA is labelled an
 * estimate in the UI so we never overpromise a hard number.
 */
export type ProvisioningStageState = 'done' | 'active' | 'pending';

export interface ProvisioningStage {
  id: 'network' | 'instances' | 'ssm' | 'detection' | 'ready';
  label: string;
  state: ProvisioningStageState;
  detail?: string | undefined;
}

const READY_STATUSES = ['ready', 'attacking', 'completed'];

export function deriveProvisioningStages(args: {
  status: string;
  vms: ReadonlyArray<RangeHealthVm>;
  vmCount: number;
}): ProvisioningStage[] {
  const total = args.vmCount || args.vms.length || 0;
  const launched = args.vms.length;
  const online = args.vms.filter((v) => v.ssmStatus === 'Online' || v.healthy).length;
  const ready = READY_STATUSES.includes(args.status);

  const done = {
    network: ready || launched > 0,
    instances: ready || (total > 0 && launched >= total),
    ssm: ready || (total > 0 && online >= total),
    detection: ready,
  };
  const flags = [done.network, done.instances, done.ssm, done.detection];
  const firstActive = flags.indexOf(false);
  const stateOf = (isDone: boolean, idx: number): ProvisioningStageState =>
    isDone ? 'done' : idx === firstActive ? 'active' : 'pending';
  const count = total ? (n: number) => `${n} / ${total}` : () => undefined;

  return [
    { id: 'network', label: 'Network ready', state: stateOf(done.network, 0), detail: 'VPC · subnets · endpoints' },
    { id: 'instances', label: 'Instances launching', state: stateOf(done.instances, 1), detail: count(launched) },
    { id: 'ssm', label: 'SSM agents online', state: stateOf(done.ssm, 2), detail: count(online) },
    { id: 'detection', label: 'Detection wiring', state: stateOf(done.detection, 3), detail: 'Wazuh enroll · Filebeat → Elastic' },
    { id: 'ready', label: 'Ready', state: ready ? 'done' : 'pending', detail: 'Consoles unlock' },
  ];
}

/** Target provisioning wall-clock, used only for the ETA estimate. */
export const PROVISIONING_TARGET_MS = 10 * 60_000;

export function provisioningEta(
  startedAt: string | null | undefined,
  now: number,
): { elapsedMs: number | null; remainingMs: number | null } {
  if (!startedAt) return { elapsedMs: null, remainingMs: null };
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) return { elapsedMs: null, remainingMs: null };
  const elapsedMs = Math.max(0, now - started);
  return { elapsedMs, remainingMs: Math.max(0, PROVISIONING_TARGET_MS - elapsedMs) };
}
